import clsx from "clsx";
import { DOCS_FORM_NAMES, QUERY_PROGRESS_SUCCEED } from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { DocsFormName } from "core/types";
import { Button, Tooltip } from "ds/ui";
import { useCopy } from "dsl/atoms/CopyButton/useCopy";
import { CopyIcon, FileSearch2Icon } from "lucide-react";
import { ReactNode } from "react";
import { useWatch } from "react-hook-form";
import { useTranslations } from "translations";
import { SourceInformation } from ".";
import { DocsFormDataMapped } from "./Forms";

const CopyButton = <T extends DocsFormName = DocsFormName>({
  fieldName,
}: {
  fieldName: keyof DocsFormDataMapped<T>;
}) => {
  const fieldValue = useWatch({
    name: fieldName as string,
  });
  const translations = useTranslations();
  const { copyMessage, copyStatus, copyToClipboard } = useCopy({
    onCopyMessage: "todo: translation",
  });

  const renderContent = () => (
    <Button
      // todo: add translation
      aria-label={translations.chat.copyMessage}
      color="primary"
      id={`copy-button-${String(fieldName)}`}
      isDisabled={!fieldValue}
      isIconOnly
      startContent={<CopyIcon />}
      variant="ghost"
      onPress={() => copyToClipboard(fieldValue)}
    />
  );

  if (copyStatus === QUERY_PROGRESS_SUCCEED) {
    return (
      <Tooltip content={copyMessage} isOpen placement="bottom">
        {renderContent()}
      </Tooltip>
    );
  }

  return renderContent();
};

export const SourceWrapper = <T extends DocsFormName = DocsFormName>({
  children,
  fieldName,
  formName,
  hideSource,
  isIconOnly,
  sourceInformation,
}: {
  children: ReactNode;
  sourceInformation: SourceInformation<T>;
  fieldName: keyof DocsFormDataMapped<T>;
  isIconOnly?: boolean;
  hideSource?: boolean;
  formName: T;
}) => {
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const translations = useTranslations();

  if (!children) {
    return null;
  }

  if ((!isDocsAndExtractActive && !isDischargeLetterActive) || hideSource) {
    return <div className="p-4">{children}</div>;
  }

  const isSelected = sourceInformation.isSourceSelected(fieldName);
  const sourceDocuments = sourceInformation.getSourceDocuments(fieldName);
  const canCopyResult = (
    [
      DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
      DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
    ] as DocsFormName[]
  ).includes(formName);

  return (
    <div
      className={clsx("flex w-full rounded-md p-4", {
        "bg-blue-50": isSelected,
        "bg-white": !isSelected,
      })}
    >
      <div
        className={clsx("flex items-center justify-between", {
          "w-[70%]": !isIconOnly,
          "w-[85%]": isIconOnly,
        })}
      >
        {children}
      </div>
      <div
        className={clsx("flex items-end px-2", {
          "justify-between": isDischargeLetterActive,
          "justify-end": !isDischargeLetterActive,
          "w-[15%]": isIconOnly,
          "w-[30%]": !isIconOnly,
        })}
      >
        {canCopyResult && <CopyButton fieldName={fieldName} />}
        {sourceDocuments.length > 0 && (
          <Button
            aria-label={translations.fileSection.fileListComponent.source}
            color="primary"
            endContent={<FileSearch2Icon />}
            id={`source-button-${String(fieldName)}`}
            isIconOnly={isIconOnly}
            variant="ghost"
            onPress={() => sourceInformation.handleSetSelectedSource(fieldName)}
          >
            {isIconOnly
              ? null
              : translations.fileSection.fileListComponent.source}
          </Button>
        )}
      </div>
    </div>
  );
};
